import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import "aos/dist/aos.css";
import AOS from "aos";

export default function ExperienceSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  // Experience data - you can add more experiences here
  const experiences = [
    {
      type: "Internship",
      title: "Software developer Intern",
      company: "JSTechno Solutions Pvt. Ltd.",
      duration: "Sep 2024 - Nov 2024 ( 3 mons )",
      responsibilities: [
        "Developed efficient backend services and created RESTful APIs using Laravel, enhancing performance and scalability.",
        "Optimized database interactions to improve data retrieval and storage processes.",
        "Collaborated with cross-functional teams to understand project requirements and deliver robust features.",
        "Contributed to building scalable web applications by leveraging the Laravel framework.",
        "Participated in code reviews and provided constructive feedback to peers.",
        "Assisted in debugging and troubleshooting issues to ensure smooth application performance.",
      ],
    },
    {
      type: "Full-time",
      title: "Software Developer (Backend heavy)",
      company: "MYSA Tech (SAAKH).",
      duration: "April 2025 - Present ( Ongoing )",
      responsibilities: [
        "Design and develop scalable, high-performance backend services using Node.js and TypeScript, ensuring type safety and maintainable code.",
        "Build, deploy, and maintain microservices with clear separation of concerns, inter-service communication (event-driven/REST), and API gateways.",
        "Work with container orchestration tools (e.g., AWS ECS/EKS, Kubernetes) to manage microservices in production.",
        "Implement CI/CD pipelines (GitHub Actions, AWS CodePipeline) for automated testing and deployment.",
        "Optimize backend services for low latency, high throughput, and efficient database queries.",
        "Collaborate with frontend developers to integrate APIs and ensure seamless user experiences.",
      ],
    },
  ];

  useEffect(() => {
    AOS.init();
    AOS.refresh();

    return () => {
      AOS.refreshHard();
    };
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    if (isAutoPlaying && !isPaused) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % experiences.length);
      }, 4000); // 4 second interval

      return () => clearInterval(interval);
    }
  }, [isAutoPlaying, isPaused, experiences.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % experiences.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + experiences.length) % experiences.length
    );
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  const getTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case "internship":
        return "bg-common-red";
      case "full-time":
        return "bg-green-600";
      case "project":
        return "bg-blue-600";
      default:
        return "bg-common-red";
    }
  };

  return (
    <>
      <div
        className="vs:py-20 md:p-10 dark:bg-[#1e386b] dark:text-white"
        id="WORK"
      >
        <div className="flex justify-center items-center text-center">
          <h2 className="text-gray-800 font-Ubuntu vs:text-2xl ts:text-4xl font-semibold pb-5 dark:text-white">
            My Technical{" "}
            <span className="text-common-red border-b-4 border-common-red">
              Experience
            </span>
          </h2>
        </div>

        <div className="grid lg:flex gap-10 justify-center items-center font-space mt-10 sm:grid sm:grid-cols-1">
          {/* Left side - Slider Content */}
          <div
            className="order-last md:order-first vs:my-5 lg:w-[50%] vs:w-3/4 vs:m-auto ml-5"
            data-aos="zoom-in-up"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* Slider Container */}
            <div className="relative overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {experiences.map((exp, index) => (
                  <div key={index} className="w-full flex-shrink-0">
                    <div className="text-lg text-gray-900 dark:text-white">
                      <button
                        className={`mb-5 ${getTypeColor(
                          exp.type
                        )} text-white font-semibold p-1 px-3 border-4 border-current hover:bg-gray-light hover:text-black rounded-lg transition-all duration-300`}
                      >
                        {exp.type}
                      </button>
                      <div className="grid gap-2">
                        <h1 className="text-2xl text-common-red font-bold font-Arimo">
                          {exp.title} at{" "}
                          <span className="border-common-red border-b-4">
                            {exp.company}
                          </span>
                        </h1>
                        <h2 className="font-semibold my-5">{exp.duration}</h2>
                      </div>
                      <ul className="space-y-4">
                        {exp.responsibilities.map((responsibility, idx) => (
                          <li
                            key={idx}
                            className="list-disc ml-5 pb-2 vs:mt-10 md:mt-0 first:vs:mt-10 first:md:mt-0"
                          >
                            {responsibility}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex justify-between items-center mt-8">
              {/* Left Arrow */}
              <button
                onClick={prevSlide}
                className="flex items-center justify-center w-12 h-12 bg-common-red hover:bg-red-700 text-white rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                aria-label="Previous experience"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Slide Indicators */}
              <div className="flex space-x-3">
                {experiences.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      currentSlide === index
                        ? "bg-common-red scale-125"
                        : "bg-gray-400 hover:bg-gray-600"
                    }`}
                    aria-label={`Go to experience ${index + 1}`}
                  />
                ))}
              </div>

              {/* Right Arrow */}
              <button
                onClick={nextSlide}
                className="flex items-center justify-center w-12 h-12 bg-common-red hover:bg-red-700 text-white rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                aria-label="Next experience"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Auto-play Control */}
            <div className="flex justify-center mt-4">
              <button
                onClick={toggleAutoPlay}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-all duration-300"
                aria-label={
                  isAutoPlaying ? "Pause auto-play" : "Start auto-play"
                }
              >
                {isAutoPlaying ? <Pause size={16} /> : <Play size={16} />}
                <span className="text-sm font-medium">
                  {isAutoPlaying ? "Pause" : "Play"}
                </span>
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mt-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
              <div
                className="bg-common-red h-1 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentSlide + 1) / experiences.length) * 100}%`,
                }}
              />
            </div>
          </div>

          {/* Right side - Image (unchanged) */}
          <div
            data-aos="zoom-in-up"
            className="order-first md:order-last flex justify-center w-3/4 md:w-1/3 m-auto"
          >
            <img
              src={"/Images/Experience-section-IMG.png"}
              alt="about"
              width={"420px"}
              className="shadow-[#dc143c_20px_20px_3px_5px] dark:border-l-4 dark:border-t-4 dark:border-gray-300"
            />
          </div>
        </div>
      </div>
    </>
  );
}
